import { OverlayContainer } from '@angular/cdk/overlay';

import { Injectable, DOCUMENT, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { Themes } from '@app/core/enums/themes.enum';
import { AppStorage } from '@app/core/enums/app-storage.enum';
import { ENV_TOKEN } from '@app/app.tokens';
import { Env } from '@app/core/interfaces/env.interface';

@Injectable()
export class ThemeService {
  private document = inject<Document>(DOCUMENT);
  private env = inject<Env>(ENV_TOKEN);
  overlayContainer = inject(OverlayContainer);

  private theme$$ = new BehaviorSubject<Themes>(Themes.Light);
  private rootElement = this.document.querySelector('html') as HTMLElement;
  readonly theme$ = this.theme$$.asObservable();

  get overlay(): HTMLElement {
    return this.overlayContainer.getContainerElement();
  }

  initTheme(theme: Themes): void {
    this.rootElement.classList.add(theme);
    this.overlay.classList.add(theme);
    this.theme$$.next(theme);
  }

  setTheme(theme: Themes): void {
    const oldTheme = this.theme$$.getValue();
    this.theme$$.next(theme);

    this.rootElement.classList.remove(oldTheme);
    this.overlay.classList.remove(oldTheme);
    this.rootElement.classList.add(theme);
    this.overlay.classList.add(theme);

    try {
      localStorage?.setItem(AppStorage.Theme, theme);
    } catch (e) {
      console.error(e);
    }
  }

  getStoredTheme(): Themes | null {
    try {
      return localStorage?.getItem(AppStorage.Theme) as Themes | null;
    } catch (e) {
      console.error(e);

      return null;
    }
  }

  getBrowserTheme(): Themes {
    return window?.matchMedia('(prefers-color-scheme: dark)')?.matches
      ? Themes.Dark
      : Themes.Light;
  }

  getTheme(): Themes {
    return this.theme$$.getValue();
  }

  canChangeTheme(): boolean {
    return this.env.themeable;
  }
}
